"use client";

import {
  collection,
  doc,
  type DocumentSnapshot,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import {
  firestoreCollectionNames,
  type CreditLedgerEntry,
  type CurrentUserProfileSummary,
} from "@reelmagic/domain";
import { getFirebaseClientServices } from "../firebase/client";

/** Shared by one-shot reads and realtime listeners — returns null until the doc exists. */
export function parseUserProfileSnapshot(
  snapshot: DocumentSnapshot,
): CurrentUserProfileSummary | null {
  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data() as Omit<CurrentUserProfileSummary, "id"> &
    Record<string, unknown>;

  return {
    ...data,
    id: snapshot.id,
    showcaseConsent:
      data.showcaseConsent === "granted" || data.showcaseConsent === "declined"
        ? data.showcaseConsent
        : null,
    showcaseConsentUpdatedAt:
      typeof data.showcaseConsentUpdatedAt === "string"
        ? data.showcaseConsentUpdatedAt
        : null,
  } as CurrentUserProfileSummary & Record<string, unknown>;
}

export async function getCurrentUserProfile(userId: string) {
  const { firestore } = getFirebaseClientServices();
  const snapshot = await getDoc(doc(firestore, firestoreCollectionNames.users, userId));

  return parseUserProfileSnapshot(snapshot);
}

export async function getCreditLedgerEntriesPage(
  userId: string,
  options?: {
    pageSize?: number;
    startAfterCreatedAt?: string | null;
  },
) {
  const pageSize = options?.pageSize ?? 12;
  const { firestore } = getFirebaseClientServices();
  const constraints = [
    where("userId", "==", userId),
    orderBy("createdAt", "desc"),
    ...(options?.startAfterCreatedAt ? [startAfter(options.startAfterCreatedAt)] : []),
    limit(pageSize + 1),
  ] as const;

  const snapshot = await getDocs(
    query(collection(firestore, firestoreCollectionNames.creditLedger), ...constraints),
  );

  const docs = snapshot.docs;
  const hasMore = docs.length > pageSize;
  const visibleDocs = hasMore ? docs.slice(0, pageSize) : docs;
  const entries = visibleDocs.map((entry) => {
    const data = entry.data() as Omit<CreditLedgerEntry, "id">;

    return {
      ...data,
      id: entry.id,
    } as CreditLedgerEntry;
  });

  return {
    entries,
    hasMore,
    nextCursor: entries.at(-1)?.createdAt ?? null,
  };
}
